import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { DocNode } from '../notes/noteSerialize';
import type { ShiftRow } from './shiftDigest';

/**
 * Reading a shift, and keeping it.
 *
 * The read is one RPC. Nothing here filters by person and nothing here could:
 * my_shift_activity takes two instants and reads auth.uid() itself, so there is
 * no user argument for a bug to widen. That is the whole reason it is a
 * function rather than seven queries stitched together in the browser.
 */

function fail(message: string): never {
  throw new Error(message);
}

export const shiftKeys = {
  all: ['shift'] as const,
  window: (from: string, to: string) => [...shiftKeys.all, from, to] as const,
};

export async function fetchShiftActivity(
  client: SupabaseClient,
  from: Date,
  to: Date,
): Promise<ShiftRow[]> {
  const { data, error } = await client.rpc('my_shift_activity', {
    p_from: from.toISOString(),
    p_to: to.toISOString(),
  });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export function useShiftActivity(window: { from: Date; to: Date } | null) {
  const { client } = useAuth();
  const from = window?.from.toISOString() ?? '';
  const to = window?.to.toISOString() ?? '';
  return useQuery({
    queryKey: shiftKeys.window(from, to),
    queryFn: () => fetchShiftActivity(client, new Date(from), new Date(to)),
    enabled: window !== null,
    // A shift that has already ended cannot change. One that is running gains
    // rows as she works, and she is the one making them — so a refetch when
    // she comes back to the tab is exactly right, and a polling interval would
    // be a query every few seconds for a screen nobody stares at.
    staleTime: 30_000,
  });
}

/**
 * Write the digest into My Notes.
 *
 * A new note each time, never an edit of an existing one. Hanneza leaned that
 * way in her brief and it is also the safer half of the choice: appending to a
 * note she may have already annotated means deciding what happens to her
 * words, and the answer people expect ("keep them, put mine after") is the one
 * that quietly duplicates when she presses the button twice.
 */
export function useSaveShiftNote() {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; doc: DocNode }): Promise<string> => {
      // user_id is set on insert because the policy's WITH CHECK demands it
      // match the caller — the same reasoning as notes/queries.ts.
      const userId = access.status === 'approved' ? access.profile.id : fail('Not signed in.');
      const { data, error } = await client
        .from('user_notes')
        .insert({
          user_id: userId,
          folder_id: null,
          title: input.title,
          content: JSON.stringify(input.doc),
        })
        .select('id')
        .single();
      if (error) {
        fail(error.message);
      }
      return data.id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}
